/**
 * ProfileSettingsPage - Personal Profile Management
 * 
 * Manages user identity: name, photo, bio, location, birthday, sports.
 * Extracted from ProfileEditPage as part of settings consolidation.
 * 
 * Related Issues: #596, #598 (Settings/Edit page consolidation)
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile, useUpdateProfile } from '../../hooks/useProfile';
import { TopBar, Button, Input, Skeleton } from '../../components';
import { Camera, User, MapPin, Calendar, Trophy, Save, X } from 'lucide-react';
import type { ProfileUpdateRequest } from '../../lib/api/profile';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const SPORTS = [
  'Running', 'Cycling', 'Swimming', 'Triathlon', 'Hiking',
  'Rock Climbing', 'Yoga', 'CrossFit', 'Gym', 'Tennis',
  'Basketball', 'Soccer', 'Other'
];

const SPORTS_OPTIONS = [
  'Trail Running', 'Mountain Biking', 'Road Cycling',
  'Open Water Swimming', 'Hiking', 'Rock Climbing',
  'Bouldering', 'Yoga', 'Pilates', 'CrossFit',
  'Weightlifting', 'Tennis', 'Pickleball', 'Basketball',
  'Soccer', 'Volleyball', 'Skiing', 'Snowboarding'
];

export default function ProfileSettingsPage() {
  const navigate = useNavigate();
  const { data: profile, isLoading } = useProfile();
  const updateProfileMutation = useUpdateProfile();

  const [formData, setFormData] = useState<ProfileUpdateRequest>({
    first_name: '',
    last_name: '',
    public_name_mode: 'full',
    tagline: '',
    birthday_day: null,
    birthday_month: null,
    location: '',
    primary_sport: '',
    secondary_sports: []
  });

  const [selectedSecondaryBits, setSelectedSecondaryBits] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (profile) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        public_name_mode: profile.public_name_mode || 'full',
        tagline: profile.tagline || '',
        birthday_day: profile.birthday_day || null,
        birthday_month: profile.birthday_month || null,
        location: profile.location || '',
        primary_sport: profile.primary_sport || '',
        secondary_sports: profile.secondary_sports || []
      });
      setSelectedSecondaryBits(new Set(profile.secondary_sports || []));
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [profile]);

  const toggleSecondarySport = (sport: string) => {
    setSelectedSecondaryBits(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sport)) {
        newSet.delete(sport);
      } else {
        newSet.add(sport);
      }
      setFormData(prevData => ({
        ...prevData,
        secondary_sports: Array.from(newSet)
      }));
      return newSet;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    try {
      await updateProfileMutation.mutateAsync(formData);
      navigate('/settings');
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-oro-bg">
        <TopBar title="Profile Settings" onBack={() => navigate('/settings')} />
        <div className="pt-4 px-4 pb-20 space-y-6">
          <div className="text-center space-y-4">
            <Skeleton variant="circular" className="w-24 h-24 mx-auto" />
            <Skeleton className="h-8 w-32 mx-auto" />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-oro-bg">
      <TopBar 
        title="Profile Settings" 
        onBack={() => navigate('/settings')}
      />
      
      <form onSubmit={handleSubmit} className="pt-4 px-4 pb-20">
        {/* Profile Picture Section */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
          <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
            <Camera className="w-5 h-5 text-accent" />
            Profile Picture
          </h2>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 bg-oro-surface-alt rounded-full flex items-center justify-center">
                {profile?.profile_image_url ? (
                  <img 
                    src={profile.profile_image_url} 
                    alt="Profile" 
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <User className="w-8 h-8 text-text-muted" />
                )}
              </div>
              <button 
                type="button"
                className="absolute -bottom-1 -right-1 bg-accent text-black rounded-full p-1.5 shadow-lg hover:bg-accent-light transition-colors"
              >
                <Camera className="w-3 h-3" />
              </button>
            </div>
            <div className="flex-1">
              <Button variant="secondary" size="sm" type="button">
                Change Photo
              </Button>
              <p className="text-xs text-text-muted mt-2">
                JPG or PNG, max 5MB
              </p>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
          <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-accent" />
            Personal Information
          </h2>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  First Name *
                </label>
                <Input
                  value={formData.first_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="John"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Last Name *
                </label>
                <Input
                  value={formData.last_name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Smith"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Public Name Display
              </label>
              <select
                value={formData.public_name_mode || 'full'}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  public_name_mode: e.target.value as typeof formData.public_name_mode 
                }))}
                className="w-full px-4 py-2 text-sm border border-oro-border rounded-lg bg-oro-surface-alt text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="full">Full Name (John Smith)</option>
                <option value="first_last_initial">First + Initial (John S.)</option>
                <option value="first_only">First Name Only (John)</option>
                <option value="anonymous">Anonymous</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Bio
              </label>
              <textarea
                value={formData.tagline || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, tagline: e.target.value }))}
                placeholder="Tell others about yourself..."
                rows={3}
                className="w-full px-4 py-2 text-sm border border-oro-border rounded-lg bg-oro-surface-alt text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Location
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
                <Input
                  value={formData.location || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Los Angeles, CA"
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Birthday */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
          <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-accent" />
            Birthday (Optional)
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Month
              </label>
              <select
                value={formData.birthday_month?.toString() || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  birthday_month: e.target.value ? parseInt(e.target.value) : null 
                }))}
                className="w-full px-4 py-2 text-sm border border-oro-border rounded-lg bg-oro-surface-alt text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Select month</option>
                {MONTHS.map((month, index) => (
                  <option key={month} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Day
              </label>
              <Input
                type="number"
                value={formData.birthday_day?.toString() || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  birthday_day: e.target.value ? parseInt(e.target.value) : null 
                }))}
                placeholder="15"
                min="1"
                max="31"
              />
            </div>
          </div>
          
          <p className="text-xs text-text-muted mt-2">
            Year is not required. This helps with birthday notifications.
          </p>
        </div>

        {/* Sports Preferences */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
          <h2 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-accent" />
            Sports & Interests
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Primary Sport
              </label>
              <select
                value={formData.primary_sport || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, primary_sport: e.target.value }))}
                className="w-full px-4 py-2 text-sm border border-oro-border rounded-lg bg-oro-surface-alt text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              >
                <option value="">Select a sport</option>
                {SPORTS.map(sport => (
                  <option key={sport} value={sport}>
                    {sport}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Other Sports (Select all that apply)
              </label>
              <div className="grid grid-cols-2 gap-2">
                {SPORTS_OPTIONS.map((sport) => (
                  <button
                    key={sport}
                    type="button"
                    onClick={() => toggleSecondarySport(sport)}
                    className={`p-2 rounded-lg border text-sm transition-colors ${
                      selectedSecondaryBits.has(sport)
                        ? 'bg-accent/10 border-accent text-accent'
                        : 'bg-oro-surface-alt border-oro-border text-text-secondary hover:border-oro-border-hover'
                    }`}
                  >
                    {sport}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={updateProfileMutation.isPending}
            disabled={updateProfileMutation.isPending}
          >
            <Save className="w-5 h-5 mr-2" />
            Save Changes
          </Button>
          
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={() => navigate('/settings')}
            disabled={updateProfileMutation.isPending}
          >
            <X className="w-5 h-5 mr-2" />
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
